import { useEffect, useId, useState, useMemo, type ChangeEvent, type DragEvent } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { TemplateOption } from '@pius/contracts';
import { parseOptionImport, generateOptionsCsvSample, type ParsedOption } from './bulkOptionParser';
import { downloadFile } from './templateBundle';
import './template-modals.css';

interface BulkOptionModalProps {
  itemLabel: string;
  existingOptions: TemplateOption[];
  isOpen: boolean;
  onClose: () => void;
  onApply: (options: TemplateOption[]) => void;
}

export function BulkOptionModal({
  itemLabel,
  existingOptions,
  isOpen,
  onClose,
  onApply,
}: BulkOptionModalProps): React.JSX.Element | null {
  const { t } = useTranslation('common');
  const fileInputId = useId();

  const [rawText, setRawText] = useState('');
  const [importMode, setImportMode] = useState<'append' | 'replace'>('append');
  const [editedOptions, setEditedOptions] = useState<ParsedOption[] | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleClose = () => {
    setRawText('');
    setImportMode('append');
    setEditedOptions(null);
    setIsDragging(false);
    onClose();
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const existingKeys = useMemo(
    () => new Set(importMode === 'append' ? existingOptions.map((o) => o.key) : []),
    [existingOptions, importMode],
  );

  const parsedResult = useMemo(() => {
    return parseOptionImport(rawText, existingKeys);
  }, [rawText, existingKeys]);

  const activeOptions = editedOptions ?? parsedResult.options;
  const validOptions = activeOptions.filter((o) => o.isValid);

  if (!isOpen) return null;

  const handleFileUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      if (content) {
        setRawText(content);
        setEditedOptions(null);
      }
    };
    reader.readAsText(file);
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileUpload(file);
  };

  const handleDrop = (e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileUpload(file);
  };

  const updateRow = (index: number, patch: Partial<ParsedOption>) => {
    const next = [...activeOptions];
    const curr = next[index];
    if (curr) {
      next[index] = { ...curr, ...patch };
      setEditedOptions(next);
    }
  };

  const removeRow = (index: number) => {
    const next = activeOptions.filter((_, i) => i !== index);
    setEditedOptions(next);
  };

  const handleDownloadSample = () => {
    const sample = generateOptionsCsvSample();
    downloadFile('options-sample.csv', sample, 'text/csv');
  };

  const handleApply = () => {
    const formatted: TemplateOption[] = validOptions.map((opt, idx) => {
      const baseOrdinal = importMode === 'append' ? existingOptions.length : 0;
      const res: TemplateOption = {
        key: opt.key,
        label: opt.label,
        ordinal: baseOrdinal + idx + 1,
      };
      if (opt.source_ref && opt.source_ref.trim() !== '') {
        res.source_ref = opt.source_ref.trim();
      }
      return res;
    });

    const finalOptions = importMode === 'append' ? [...existingOptions, ...formatted] : formatted;

    onApply(finalOptions);
    handleClose();
  };

  return createPortal(
    <div
      className="tpl-modal__overlay"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div className="tpl-modal tpl-modal--large">
        <header className="tpl-modal__head">
          <div>
            <p className="tpl-eyebrow">{t('templates.bulk_importer_eyebrow')}</p>
            <h2 className="tpl-modal__title">
              {t('templates.bulk_importer_title', { item: itemLabel })}
            </h2>
          </div>
          <button
            type="button"
            className="tpl-modal__close-btn"
            onClick={handleClose}
            aria-label={t('action.close', 'Close')}
          >
            ✕
          </button>
        </header>

        <p className="tpl-modal__lede">{t('templates.bulk_importer_lede')}</p>

        <div className="tpl-modal__body">
          <div className="tpl-bulk__controls">
            <div className="tpl-bulk__upload-row">
              <label
                htmlFor={fileInputId}
                className={`tpl-bulk__dropzone ${isDragging ? 'tpl-bulk__dropzone--active' : ''}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
              >
                <span>{t('templates.bulk_dropzone')}</span>
              </label>
              <input
                id={fileInputId}
                type="file"
                accept=".csv,.tsv,.txt"
                className="tpl-bulk__file-input"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />

              <button
                type="button"
                className="tpl-btn tpl-btn--ghost"
                onClick={handleDownloadSample}
              >
                📥 {t('templates.bulk_download_sample')}
              </button>
            </div>

            <label className="tpl-modal__field">
              <span className="tpl-modal__field-label">
                {t('templates.bulk_paste_label')}
                {rawText && (
                  <span className="tpl-bulk__format-badge">
                    {t(`templates.detected_${parsedResult.delimiterDetected}`)}
                    {parsedResult.hasHeader ? ` · ${t('templates.header_detected')}` : ''}
                  </span>
                )}
              </span>
              <textarea
                className="tpl-bulk__textarea tpl-modal__mono"
                rows={6}
                value={rawText}
                placeholder={`key,label,source_ref\ngen_1_26,"Genesis 1:26-28a (Male and female)",801-1\ntob_8_4b,"Tobit 8:4b-8 (Allow us)",801-3\n\n-- or simply paste titles one per line --\nGenesis 1:26-28a\nTobit 8:4b-8\nPsalm 34: Taste and see`}
                onChange={(e) => {
                  setRawText(e.target.value);
                  setEditedOptions(null);
                }}
              />
            </label>

            {activeOptions.length > 0 && (
              <div className="tpl-bulk__mode-select">
                <span className="tpl-bulk__mode-label">{t('templates.import_mode_label')}:</span>
                <label className="tpl-bulk__radio">
                  <input
                    type="radio"
                    name="importMode"
                    value="append"
                    checked={importMode === 'append'}
                    onChange={() => setImportMode('append')}
                  />
                  <span>
                    {t('templates.import_mode_append', { count: existingOptions.length })}
                  </span>
                </label>
                <label className="tpl-bulk__radio">
                  <input
                    type="radio"
                    name="importMode"
                    value="replace"
                    checked={importMode === 'replace'}
                    onChange={() => setImportMode('replace')}
                  />
                  <span>{t('templates.import_mode_replace')}</span>
                </label>
              </div>
            )}
          </div>

          {activeOptions.length > 0 && (
            <div className="tpl-bulk__preview-box">
              <div className="tpl-bulk__preview-head">
                <span className="tpl-bulk__preview-title">
                  {t('templates.preview_title')} ({validOptions.length}{' '}
                  {t('templates.valid_options')})
                </span>
                <span className="tpl-bulk__preview-hint">{t('templates.preview_edit_hint')}</span>
              </div>

              <div className="tpl-bulk__table-wrapper">
                <table className="tpl-bulk__table">
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}>#</th>
                      <th style={{ width: '220px' }}>{t('templates.col_key')}</th>
                      <th>{t('templates.col_label')}</th>
                      <th style={{ width: '150px' }}>{t('templates.col_ref')}</th>
                      <th style={{ width: '40px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeOptions.map((opt, idx) => (
                      <tr key={idx} className={!opt.isValid ? 'tpl-bulk__row--invalid' : ''}>
                        <td className="tpl-bulk__cell-num">{idx + 1}</td>
                        <td>
                          <input
                            className="tpl-modal__mono tpl-bulk__cell-input"
                            value={opt.key}
                            onChange={(e) => updateRow(idx, { key: e.target.value })}
                          />
                        </td>
                        <td>
                          <input
                            className="tpl-bulk__cell-input"
                            value={opt.label}
                            onChange={(e) => updateRow(idx, { label: e.target.value })}
                          />
                        </td>
                        <td>
                          <input
                            className="tpl-bulk__cell-input"
                            value={opt.source_ref ?? ''}
                            placeholder="Optional"
                            onChange={(e) => updateRow(idx, { source_ref: e.target.value })}
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            className="tpl-btn tpl-btn--ghost"
                            onClick={() => removeRow(idx)}
                            title={t('action.remove')}
                          >
                            ×
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <footer className="tpl-modal__foot">
          <button type="button" className="tpl-btn tpl-btn--ghost" onClick={handleClose}>
            {t('action.cancel', 'Cancel')}
          </button>
          <button
            type="button"
            className="tpl-btn tpl-btn--primary"
            disabled={validOptions.length === 0}
            onClick={handleApply}
          >
            {importMode === 'append'
              ? t('templates.btn_add_count_options', { count: validOptions.length })
              : t('templates.btn_replace_count_options', { count: validOptions.length })}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
